/**
 * LazyPhoneInput - Lazy-loaded wrapper for react-phone-number-input
 *
 * This component lazy loads the heavy PhoneInput component (427KB) to improve
 * initial page load performance. The component is only loaded when actually needed.
 *
 * Usage: Replace direct PhoneInput imports with this component
 * Related files: login-form.tsx, checkout/route.tsx, register/route.tsx
 */

import React, { useState, useEffect } from 'react';

interface LazyPhoneInputProps {
  value?: string;
  onChange?: (value?: string) => void;
  placeholder?: string;
  className?: string;
  international?: boolean;
  defaultCountry?: string;
  [key: string]: any; // Allow any additional props
}

/**
 * Lazy-loaded PhoneInput with stable fallback
 */
export default function LazyPhoneInput(props: LazyPhoneInputProps) {
  const [PhoneInputComponent, setPhoneInputComponent] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const loadPhoneInput = async () => {
      try {
        // Load CSS and component in parallel
        await import('react-phone-number-input/style.css');
        const phoneInputModule = await import('react-phone-number-input');

        if (mounted) {
          setPhoneInputComponent(() => phoneInputModule.default);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Failed to load PhoneInput:', error);
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadPhoneInput();

    return () => {
      mounted = false;
    };
  }, []);

  // Stable fallback matching other form fields exactly — no flag to avoid mismatch flash
  if (isLoading || !PhoneInputComponent) {
    return (
      <input
        type="tel"
        placeholder={props.placeholder || 'Enter phone number'}
        className={props.className || 'w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-sm'}
        value={props.value || ''}
        onChange={(e) => props.onChange?.(e.target.value)}
      />
    );
  }

  // The country dropdown is a native <select> that (like all native selects) doesn't open on
  // Enter. Use the picker API so keyboard users can open it (Space/Arrows work natively too).
  const handleCountryKeyDown = (e: React.KeyboardEvent<HTMLSelectElement>) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      const el = e.currentTarget as HTMLSelectElement & { showPicker?: () => void };
      if (typeof el.showPicker === 'function') {
        e.preventDefault();
        try {
          el.showPicker();
        } catch {
          // showPicker can throw without a user gesture — ignore.
        }
      }
    }
    props.countrySelectProps?.onKeyDown?.(e);
  };

  return (
    <PhoneInputComponent
      {...props}
      countrySelectProps={{ ...(props.countrySelectProps || {}), onKeyDown: handleCountryKeyDown }}
    />
  );
}
